/**
 * API Client
 * Type-safe API client using openapi-fetch and Clerk authentication
 */

import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from '@/types/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Global token getter function (will be set by Clerk auth hook)
let getAuthToken: (() => Promise<string | null>) | null = null;

/**
 * Set the token getter function
 * This is called by the AuthProvider to inject Clerk's getToken function
 */
export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  getAuthToken = getter;
}

/**
 * Authentication middleware - adds Clerk Bearer token to requests
 */
const authMiddleware: Middleware = {
  async onRequest(req) {
    // Get token from Clerk via injected getter
    if (getAuthToken) {
      try {
        const token = await getAuthToken();
        if (token) {
          req.headers.set('Authorization', `Bearer ${token}`);
        }
      } catch (error) {
        console.error('Failed to get auth token:', error);
      }
    }
    return req;
  },

  async onResponse(res) {
    return res;
  },
};

/**
 * Create API client instance
 */
export const apiClient = createClient<paths>({
  baseUrl: `${API_BASE_URL}/api`,
});

// Add auth middleware
apiClient.use(authMiddleware);

/**
 * Legacy token functions (kept for backward compatibility but deprecated)
 */
export function getTokens(): { accessToken: string | null; refreshToken: string | null } {
  return { accessToken: null, refreshToken: null };
}

export function setTokens(_accessToken: string, _refreshToken: string): void {
  // No-op: Clerk manages tokens
}

export function clearTokens(): void {
  // No-op: Clerk manages tokens
}

/**
 * Typed API client for easy imports
 */
export default apiClient;
